import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {IAssignQuoteAction} from "../../../../../app/interfaces/quote/IAssignQuoteAction";
import {useQuoteApi} from "../../../../../app/api/quote";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import {toast} from "react-toastify";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import {isAxiosError} from "axios";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import AssignQuoteForm from "./AssignQuoteForm/AssignQuoteForm";
import SelectedQuotes from "../../form/components/SelectedQuotes/SelectedQuotes";

type AssignQuoteProps = {
    id: string | Array<string>;
    close?: () => void;
}

const AssignQuote: React.FC<AssignQuoteProps> = ({
                                                     id,
                                                     close
                                                 }) => {
    const {t} = useTranslation();
    const [model, setModel] = useState<IAssignQuoteAction | null>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
    const [disabled, setDisabled] = useState<boolean>(false);

    const {
        getAssignQuoteData: {
            isLoading,
            cancel,
            query: getAssignQuoteData
        },
        assignQuote: {
            cancel: cancelAssignQuote,
            mutation: assignQuote
        }
    } = useQuoteApi();

    useEffect(() => {
        if (!id || id === '') {
            return;
        }

        (async () => {
            try {
                const response = await getAssignQuoteData(id);
                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch {
                //ignore
            }
        })();

        return () => {
            cancel();
            cancelAssignQuote();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async (model: IAssignQuoteAction): Promise<void> => {
        setDisabled(prev => !prev);

        try {
            const response = await assignQuote(model);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRows',
                        rows: [...id].map((item) => {
                            return {
                                id: item,
                                isRowLoading: true
                            };
                        })
                    }
                }));

                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("shared.labels.quote")} ${model.orderReleaseGid !== '' ? model.orderReleaseGid.split(';')[0] : ''}`}/>);
            }
        } catch (e) {
            if (isAxiosError(e)) {
                toast.error(e.message);
            }
        }

        setDisabled(prev => !prev);
    }

    if ((Array.isArray(id) && id.length <= 0) || id === '') {
        return null;
    }

    if (isLoading || model === null) {
        return (
            <FormLoadingScreen height={`200px`} style={{padding: '5px'}}/>
        )
    }

    return (
        <div className={'h100 w100'}>
            <div className={'dialog-default-content-container'}>
                {Array.isArray(id) && id.length > 1 && <SelectedQuotes ids={id}/>}
                <AssignQuoteForm model={model}
                                 disabled={disabled}
                                 setIsSubmitDisabled={setIsSubmitDisabled}
                                 save={save}/>
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={isSubmitDisabled || disabled}
                        form='assign-quote-form'
                        type='submit'
                        style={{minWidth: '150px'}}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.assign")}
                </Button>
            </div>
        </div>
    );
};

export default AssignQuote;